import { useCallback, useEffect, useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import {
  Grid,
  Container,
  Typography,
  Stack,
  Box,
  Alert,
  Skeleton,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import useIsMountedRef from 'use-is-mounted-ref';
import { useDispatch, useSelector } from 'react-redux';
// sections
import { AppWidgetSummary, AssetChartData } from '../../../sections/@dashboard/app';
import { Page } from '../../../components';
import { getDashboardData } from '../store/dashboardActions';
import { RecentLoans } from '../components';
import AppCurrentVisits from '../components/AppCurrentVisits';
import { fCurrency, fNumber } from '../../../utils/formatNumber';

// ----------------------------------------------------------------------

const LoadingSkeleton = () => (
  <Grid container spacing={3}>
    {[...Array(4)].map((_, index) => (
      <Grid item xs={12} sm={6} md={3} key={index}>
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="40%" />
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const ErrorAlert = ({ message, onRetry }) => (
  <Alert
    severity="error"
    action={
      <Tooltip title="Retry">
        <IconButton color="inherit" size="small" onClick={onRetry}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    }
    sx={{ mb: 3 }}
  >
    {message || 'Failed to load dashboard data. Please try again.'}
  </Alert>
);

const DashboardStats = ({ assetCount, totalAssetValue, assetsNetValue, totalDepreciation, isLoading }) => {
  // Validate and provide fallback values
  const validatedAssetCount = Number.isFinite(assetCount) ? assetCount : 0;
  const validatedTotalAssetValue = Number.isFinite(totalAssetValue) ? totalAssetValue : 0;
  const validatedAssetsNetValue = Number.isFinite(assetsNetValue) ? assetsNetValue : 0;
  const validatedTotalDepreciation = Number.isFinite(totalDepreciation) ? totalDepreciation : 0;

  const stats = [
    {
      title: 'Total Assets',
      total: validatedAssetCount,
      color: 'success',
      icon: 'material-symbols:person-book',
      format: fNumber,
      tooltip: 'Total number of assets in the system',
    },
    {
      title: 'Total Purchase Cost',
      total: validatedTotalAssetValue,
      color: 'info',
      icon: 'uil:money-insert',
      format: fCurrency,
      tooltip: 'Total cost of all assets purchased',
    },
    {
      title: 'Total Depreciation',
      total: validatedTotalDepreciation,
      color: 'warning',
      icon: 'ph:drop-half-bottom-fill',
      format: fCurrency,
      tooltip: 'Total accumulated depreciation',
    },
    {
      title: 'Net Book Value',
      total: validatedAssetsNetValue,
      color: 'error',
      icon: 'ic:outline-watch-later',
      format: fCurrency,
      tooltip: 'Current net book value of all assets',
    },
  ];

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <Grid container spacing={3}>
      {stats.map((stat, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Tooltip title={stat.tooltip} arrow>
            <Box>
              <AppWidgetSummary
                title={stat.title}
                total={stat.format ? stat.format(stat.total) : stat.total}
                color={stat.color}
                icon={stat.icon}
                sx={{
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: (theme) => theme.customShadows.z24,
                  },
                }}
              />
            </Box>
          </Tooltip>
        </Grid>
      ))}
    </Grid>
  );
};

const DashboardCharts = ({ graphData, costPerClass, theme, isLoading }) => {
  if (isLoading) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={8}>
          <Card>
            <CardContent>
              <Skeleton variant="rectangular" height={400} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Skeleton variant="rectangular" height={400} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  // Validate and process chart data
  const chartData =
    graphData?.chart_data?.map(({ category, count }) => ({
      name: category || 'Unknown',
      type: 'line',
      fill: 'solid',
      data: Array.isArray(count) && count.length > 0 ? count : Array(12).fill(0),
    })) || [];

  // Validate cost per class data
  const validCostPerClass = Array.isArray(costPerClass) ? costPerClass : [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6} lg={8}>
        <AssetChartData
          title="Asset Purchase Statistics"
          subheader="Purchase insights this year"
          chartLabels={(graphData && graphData?.chart_labels) || []}
          chartData={chartData}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <AppCurrentVisits
          title="Cost Per Category"
          subheader="Distribution of asset costs by category"
          chartData={validCostPerClass}
          chartColors={[
            theme.palette.primary.main,
            theme.palette.info.main,
            theme.palette.warning.main,
            theme.palette.error.main,
            theme.palette.secondary.main,
          ]}
        />
      </Grid>
    </Grid>
  );
};

export default function Dashboard() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Selectors
  const {
    asset_count: assetCount,
    total_asset_value: totalAssetValue,
    assets_net_value: assetsNetValue,
    total_depreciation: totalDepreciation,
    recentAssets,
    graph_data: graphData,
    cost_per_class: costPerClass,
    isLoading,
    message: errorMessage,
  } = useSelector((state) => state.dashboardReducer);

  const isMountedRef = useIsMountedRef();

  const getStatistics = useCallback(
    async (showRefresh = false) => {
      if (isMountedRef.current) {
        if (showRefresh) {
          setIsRefreshing(true);
        }
        try {
          dispatch(getDashboardData());
        } finally {
          if (showRefresh) {
            setIsRefreshing(false);
          }
        }
      }
    },
    [isMountedRef, dispatch]
  );

  const handleRefresh = useCallback(() => {
    getStatistics(true);
  }, [getStatistics]);

  useEffect(() => {
    getStatistics();
  }, [getStatistics]);

  const hasError = Boolean(errorMessage);
  const showLoading = isLoading && !isRefreshing;

  return (
    <Page title="Dashboard">
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ mb: 1 }}>
                Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overview of your fixed assets management system
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip icon={<TrendingUpIcon />} label="Real-time Data" color="success" variant="outlined" size="small" />
              <Tooltip title="Refresh Dashboard">
                <IconButton onClick={handleRefresh} disabled={isRefreshing} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        {/* Error Alert */}
        {hasError && <ErrorAlert message={errorMessage} onRetry={handleRefresh} />}

        {/* Dashboard Stats */}
        <Box sx={{ mb: 4 }}>
          <DashboardStats
            assetCount={assetCount}
            totalAssetValue={totalAssetValue}
            assetsNetValue={assetsNetValue}
            totalDepreciation={totalDepreciation}
            isLoading={showLoading}
          />
        </Box>

        {/* Charts Section */}
        <Box sx={{ mb: 4 }}>
          <DashboardCharts graphData={graphData} costPerClass={costPerClass} theme={theme} isLoading={showLoading} />
        </Box>

        {/* Recent Activity Section */}
        <Box>
          <Stack>
            <RecentLoans recentAssets={recentAssets} />
          </Stack>
        </Box>
      </Container>
    </Page>
  );
}
